import React from 'react';
import { TagGroup } from '../hooks/api/useTagGroups';
import {
  CaregivingIcon,
  FaithIcon,
  FinancesIconTag,
  GriefIcon,
  IdentityIcon,
  MedicalIcon,
  OverwhelmIcon,
  RelationshipsIcon,
  SchoolIcon,
  WorkIcon,
} from '../components/icons/topic-icons/topic-icons';
import {
  RelationshipsTopicCard,
  GriefTopicCard,
  IdentityTopicCard,
  WorkTopicCard,
  FinancesTopicCard,
  SchoolTopicCard,
  MedicalTopicCard,
  OverwhelmTopicCard,
  CaregivingTopicCard,
  FaithTopicCard,
  CaregivingCircle,
  FaithCircle,
  FinancesCircle,
  GriefCircle,
  IdentityCircle,
  MedicalCircle,
  OverwhelmCircle,
  RelationshipsCircle,
  SchoolCircle,
  WorkCircle,
} from '../assets/images/svgs/topics';

// Mapping from tag group keys to icon React components (themeable inline SVGs)
const TOPIC_ICON_COMPONENT_MAP: Record<string, React.FC<{ isSelected?: boolean }>> = {
  RELATIONSHIP_ISSUES: RelationshipsIcon,
  GRIEF_AND_LOSS: GriefIcon,
  IDENTITY_AND_BELONGING: IdentityIcon,
  WORK_STRESS: WorkIcon,
  FINANCIAL_STRESS: FinancesIconTag,
  SCHOOL_STRESS: SchoolIcon,
  MEDICAL_CONDITIONS: MedicalIcon,
  OVERWHELMED_AND_COPING: OverwhelmIcon,
  CAREGIVING: CaregivingIcon,
  FAITH: FaithIcon,
};

const TOPIC_CIRCLE_MAP: Record<string, string> = {
  RELATIONSHIP_ISSUES: RelationshipsCircle,
  GRIEF_AND_LOSS: GriefCircle,
  IDENTITY_AND_BELONGING: IdentityCircle,
  WORK_STRESS: WorkCircle,
  FINANCIAL_STRESS: FinancesCircle,
  SCHOOL_STRESS: SchoolCircle,
  MEDICAL_CONDITIONS: MedicalCircle,
  OVERWHELMED_AND_COPING: OverwhelmCircle,
  CAREGIVING: CaregivingCircle,
  FAITH: FaithCircle,
};

/** Custom display title for assembled topic cards (when different from tag group name) */
const TOPIC_CARD_TITLE_MAP: Record<string, string> = {
  RELATIONSHIP_ISSUES: 'Strengthen\nRelationships',
  GRIEF_AND_LOSS: 'Find Strength\nAfter Loss',
  IDENTITY_AND_BELONGING: 'Deepen\nSelf-Understanding',
  WORK_STRESS: 'Create Work\nBalance',
  FINANCIAL_STRESS: 'Build Financial Confidence',
  SCHOOL_STRESS: 'Ease School\nStress',
  MEDICAL_CONDITIONS: 'Process Health\nChallenges',
  OVERWHELMED_AND_COPING: 'Feel More\nGrounded',
  CAREGIVING: 'Balance\nCaregiving',
  FAITH: 'Reconnect with\nmy Faith',
};

const TOPIC_SHORT_DESCRIPTION_MAP: Record<string, string> = {
  RELATIONSHIP_ISSUES: 'Navigating personal relationships',
  GRIEF_AND_LOSS: 'Coping with loss & processing grief',
  IDENTITY_AND_BELONGING: 'Understanding yourself & your community',
  WORK_STRESS: 'Navigating workplace dynamics',
  FINANCIAL_STRESS: 'Handling financial life events',
  SCHOOL_STRESS: 'Managing the school experience',
  MEDICAL_CONDITIONS: 'Facing health-related events',
  OVERWHELMED_AND_COPING: 'Feeling overwhelmed or stressed',
  CAREGIVING: 'Supporting loved ones & yourself',
  FAITH: 'Reflecting on faith & spirituality',
};

/** Tag group keys that use assembled card components instead of PNG */
const TOPIC_CARD_COMPONENT_KEYS = new Set([
  'RELATIONSHIP_ISSUES',
  'GRIEF_AND_LOSS',
  'IDENTITY_AND_BELONGING',
  'WORK_STRESS',
  'FINANCIAL_STRESS',
  'SCHOOL_STRESS',
  'MEDICAL_CONDITIONS',
  'OVERWHELMED_AND_COPING',
  'CAREGIVING',
  'FAITH',
]);

/**
 * Get the assembled card component for a tag group key, if it uses one
 */
export const getTopicCardComponentByKey = (
  key: string,
): React.FC<{ title: string; className?: string }> | undefined => {
  const normalizedKey = key.toUpperCase();
  if (!TOPIC_CARD_COMPONENT_KEYS.has(normalizedKey)) return undefined;
  if (normalizedKey === 'RELATIONSHIP_ISSUES') return RelationshipsTopicCard;
  if (normalizedKey === 'GRIEF_AND_LOSS') return GriefTopicCard;
  if (normalizedKey === 'IDENTITY_AND_BELONGING') return IdentityTopicCard;
  if (normalizedKey === 'WORK_STRESS') return WorkTopicCard;
  if (normalizedKey === 'FINANCIAL_STRESS') return FinancesTopicCard;
  if (normalizedKey === 'SCHOOL_STRESS') return SchoolTopicCard;
  if (normalizedKey === 'MEDICAL_CONDITIONS') return MedicalTopicCard;
  if (normalizedKey === 'OVERWHELMED_AND_COPING') return OverwhelmTopicCard;
  if (normalizedKey === 'CAREGIVING') return CaregivingTopicCard;
  if (normalizedKey === 'FAITH') return FaithTopicCard;
  return undefined;
};

/**
 * Get the card display title for a tag group key (for assembled cards)
 */
export const getTopicCardTitleByKey = (key: string): string | undefined => {
  const normalizedKey = key.toUpperCase();
  return TOPIC_CARD_TITLE_MAP[normalizedKey];
};

/**
 * Get the hero circle image path for a tag group by its key
 */
export const getTopicCircleByKey = (key: string): string | undefined => {
  const normalizedKey = key.toUpperCase();
  return TOPIC_CIRCLE_MAP[normalizedKey];
};

/**
 * Get the assembled card component for a tag group, if it uses one
 */
export const getTopicCardComponent = (
  tagGroup: TagGroup | undefined,
): React.FC<{ title: string; className?: string }> | undefined => {
  if (!tagGroup?.key) return undefined;
  return getTopicCardComponentByKey(tagGroup.key);
};

/**
 * Get the card display title for a tag group (for assembled cards)
 * Falls back to tag group name if no custom title
 */
export const getTopicCardTitle = (tagGroup: TagGroup | undefined): string => {
  if (!tagGroup?.key) return '';
  const customTitle = getTopicCardTitleByKey(tagGroup.key);
  return customTitle ?? tagGroup.name ?? '';
};

/**
 * Get the hero circle image path for a tag group object
 */
export const getTopicCircle = (tagGroup: TagGroup | undefined): string | undefined => {
  if (!tagGroup?.key) return undefined;
  return getTopicCircleByKey(tagGroup.key);
};

/**
 * Get icon React element for a tag group object
 * Uses themeable inline SVG components (inherits --color-primary)
 */
export const getTopicIconElement = (tagGroup: TagGroup | undefined): React.ReactNode | undefined => {
  if (!tagGroup?.key) return undefined;
  const IconComponent = TOPIC_ICON_COMPONENT_MAP[tagGroup.key.toUpperCase()];
  if (!IconComponent) return undefined;
  return <IconComponent isSelected={false} />;
};

/**
 * Get icon React element for a tag group by key
 */
export const getTopicIconElementByKey = (key: string): React.ReactNode | undefined => {
  const normalizedKey = key.toUpperCase();
  const IconComponent = TOPIC_ICON_COMPONENT_MAP[normalizedKey];
  if (!IconComponent) return undefined;
  return <IconComponent isSelected={false} />;
};

/**
 * Get the short description for a tag group by its name (display name)
 * Requires a tag groups array to look up the tag group by name
 */
export const getTopicShortDescriptionByName = (name: string, tagGroups: TagGroup[]): string | undefined => {
  const tagGroup = tagGroups.find((tg) => tg.name === name);
  if (!tagGroup?.key) return undefined;
  return TOPIC_SHORT_DESCRIPTION_MAP[tagGroup.key];
};

/** Mapping from slug to tag group key */
export const slugToTagGroupKey: Record<string, string> = {
  relationship_issues: 'RELATIONSHIP_ISSUES',
  grief_and_loss: 'GRIEF_AND_LOSS',
  identity_and_belonging: 'IDENTITY_AND_BELONGING',
  work_stress: 'WORK_STRESS',
  financial_stress: 'FINANCIAL_STRESS',
  school_stress: 'SCHOOL_STRESS',
  health_wellness: 'MEDICAL_CONDITIONS',
  overwhelmed_and_coping: 'OVERWHELMED_AND_COPING',
  caregiving: 'CAREGIVING',
  faith: 'FAITH',
};

/** Inverse of slugToTagGroupKey – use for building browse topic URLs from tag group key */
export const tagGroupKeyToSlug: Record<string, string> = Object.fromEntries(
  Object.entries(slugToTagGroupKey).map(([slug, key]) => [key, slug]),
);

/** Ordered slugs for browse-by-topic links (home page goals, dropdown, etc.) */
export const BROWSE_TOPIC_SLUGS: string[] = [
  'relationship_issues',
  'grief_and_loss',
  'identity_and_belonging',
  'work_stress',
  'financial_stress',
  'school_stress',
  'health_wellness',
  'overwhelmed_and_coping',
  'caregiving',
  'faith',
];

/**
 * Get the URL slug for a tag group key (for browse topic links).
 * Returns the slug if mapped, otherwise the key lowercased for backwards compatibility.
 */
export const getTopicSlugByKey = (key: string): string => {
  const normalizedKey = key.toUpperCase();
  return tagGroupKeyToSlug[normalizedKey] ?? key.toLowerCase();
};
